<template>
    <p-modal
        ref="printLabelModal"
        name="printLabelModal"
        :icon-plus="false"
        size="lg"
        :label="'Print Label: ' + this.productFamily.description"
        @ok.prevent="handleModalOk"
        @show="loadData"
        @shown="setFocus">
        <p-form ref="form">
            <template v-slot:default>
                <div>
                    <b-row class="mb-2 align-items-end">
                        <b-col>
                            Price Tag Type: {{ tagType }}
                        </b-col>
                        <b-col
                            v-if="productFamily.isMatrix"
                            cols="12"
                            lg="4"
                            class="d-flex align-items-center">
                            <span class="mr-2 text-nowrap">Print Qty (All Products):</span>
                            <p-number
                                input-type="integer"
                                name="All Print Qty"
                                rules="min_value:0|max_value:32000"
                                v-model="printQtyAllProducts"
                                ref="allQtyInput"
                                compact-format
                                @input="onPrintQtyChange()"
                            />
                        </b-col>
                        <b-col cols="12" lg="2" class="d-flex align-items-end">
                            <p-button variant="outline-secondary" @click="useSohQty" size="sm">Use SOH Qty</p-button>
                        </b-col>
                        <span id="tagContent" v-html="tagContent" />
                    </b-row>
                    <b-row style="max-height:300px; overflow:auto">
                        <b-col>
                            <p-table
                                ref="SKUTable"
                                :items="printProducts"
                                :fields="fieldsSKU"
                                :enableRowSelection="false"
                                :per-page="500"
                                class="wide mt-2"
                                outlined
                                id="skuTable"
                                @sort-by-change="gridSortKey = $event"
                                @sort-desc-change="gridSortDesc = $event">
                                <template
                                    v-slot:cell(qty)="{
                                        value,
                                        item,
                                        index
                                    }">
                                    <p-number
                                        input-type="integer"
                                        name="Print Qty"
                                        :rules="'max_value:32000|min_value:' + minQty + '|' + requiredQty"
                                        @input="disableCheckPrint()"
                                        compact-format                                        
                                        v-model.number="item.qty"
                                    />
                                </template>
                            </p-table>
                        </b-col>
                    </b-row>
                </div>
            </template>
        </p-form>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button variant="outline-primary" @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button variant="primary" @click="ok" class="ms-2" :disabled="!enablePrint"
                        >Print</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>
<script>
import StoreListManager from '@/components/mixins/StoreListManager.js';
import axios from 'axios';
import printJS from 'print-js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import $ from '@/components/Common/wrapper.js';

export default {
    mixins: [StoreListManager],
    props: {
        productFamily: {
            type: Object
        },
        product: {
            type: Object
        },
        products: {
            type: Array
        }
    },
    data() {
        return {
            locationId: null,
            labelQty: 1,
            printQtyAllProducts: null,
            selectListOptions: {
                priceTagTypes: []
            },
            tagContent: null,
            printProducts: [],
            gridSortKey: null,
            gridSortDesc: false,
            fieldsSKU: [
                {
                    key: 'description',
                    label: 'Description'
                },
                {
                    key: 'defaultBarcode',
                    label: 'Barcode'
                },
                {
                    key: 'gmVariant1',
                    label: 'Variant 1',
                    sortable: true,
                    sortByFormatted: (value, key, item) => String(this.variantSortOrder(item, 'gmVariant1')).padStart(16, '0')
                },
                {
                    key: 'gmVariant2',
                    label: 'Variant 2',
                    sortable: true,
                    sortByFormatted: (value, key, item) => String(this.variantSortOrder(item, 'gmVariant2')).padStart(16, '0')
                },
                {
                    key: 'qty',
                    label: 'Print Qty',
                    thClass: 'text-start',
                    tdClass: 'text-end'
                },
                {
                    key: 'sohQty',
                    label: 'SOH Qty',
                    thClass: 'text-left',
                    tdClass: 'text-left'                
                }
            ],
            enablePrint:false
        };
    },
    computed: {
        tagType() {
            return this.selectListOptions.priceTagTypes.find(
                x => x.value === this.product.priceTagTypeId
            )?.text;
        },
        requiredQty() {
            return this.products.length <= 1 ? 'required' : '';
        },
        minQty(){
            return this.products.length <= 1 ? 1 : 0;
        }
    },
    async mounted() {
        this.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
        await this.loadData();
    },
    watch: {
        $route: function(to, from) {
            if (to !== from) {
                this.loadData();
            }
        }
    },
    methods: {
        async loadData() {
            await this.$nextTick();

            this.printProducts = JSON.parse(JSON.stringify(this.products))
                .sort(this.compareByVariantSortOrder);
            // Default every row's Print Qty to 1 (one label per SKU), and surface
            // the current location's stock-on-hand as sohQty (the table column and
            // "Use SOH Qty" button both read this; it isn't on the raw product).
            this.printProducts.forEach(x => {
                x.qty = 1;
                x.description = this.productFamily.description;
                x.sohQty = x.productInventories?.[0]?.stockOnHand ?? 0;
            });
            // Matrix products show the bulk "Print Qty (All Products)" field —
            // default it to 1 to match the per-row default.
            this.printQtyAllProducts = this.productFamily.isMatrix ? 1 : null;

            this.disableCheckPrint();
        },
        variantSortOrder(sku, variantKey) {
            return sku[variantKey + 'SortOrder'] ?? Number.MAX_SAFE_INTEGER;
        },
        compareByVariantSortOrder(firstSku, secondSku) {
            const variant1SortOrderDifference = this.variantSortOrder(firstSku, 'gmVariant1') - this.variantSortOrder(secondSku, 'gmVariant1');
            if (variant1SortOrderDifference !== 0)
                return variant1SortOrderDifference;

            const variant2SortOrderDifference = this.variantSortOrder(firstSku, 'gmVariant2') - this.variantSortOrder(secondSku, 'gmVariant2');
            if (variant2SortOrderDifference !== 0)
                return variant2SortOrderDifference;

            return (firstSku.sku ?? 0) - (secondSku.sku ?? 0);
        },
        // Mirrors the grid's sort so labels print in the order the rows are displayed. printProducts is
        // already in the default Variant 1 / Variant 2 order and the sort is stable, so ties keep that order
        // in both directions, exactly as the grid shows them.
        printProductsInGridOrder() {
            if (!this.gridSortKey)
                return [...this.printProducts];

            const direction = this.gridSortDesc ? -1 : 1;
            return [...this.printProducts]
                .sort((firstSku, secondSku) => direction * (this.variantSortOrder(firstSku, this.gridSortKey) - this.variantSortOrder(secondSku, this.gridSortKey)));
        },
        setFocus() {
            this.$refs['allQtyInput']?.focus();
            $(document).ready(function() {
                let rows = $('#skuTable tr');

                for(let row = 0; row < rows.length; row++)
                {
                    rows[row].removeAttribute('tabIndex');
                }                
            });
        },
        handleModalOk: async function() {
            if (!(await this.$refs.form.validate())) {
                return;
            }

            let printProducts = this.printProductsInGridOrder()
                .filter(x => x.qty != null && x.qty != 0);

            await axios
                .post('products/printpricetags', {
                    products: printProducts,
                    locationId: this.locationId
                })
                .then(result => {
                    this.tagContent = result.data;
                    this.$nextTick(() => {
                        printJS('tagContent', 'html', '#tagContent: visible;');
                        this.tagContent = null;
                        this.$refs.printLabelModal.hide();
                    });
                });
        },
        onPrintQtyChange() {
            console.log('onPrintQtyChange ', this.printQtyAllProducts);
            this.printProducts.map(x => (x.qty = this.printQtyAllProducts));            
            this.disableCheckPrint();
        },       
        disableCheckPrint(){
            this.enablePrint = this.printProducts.some(x => x.qty != null && x.qty != 0);
        },
        useSohQty(){
            this.printProducts.forEach(printProduct => {
                printProduct.qty = printProduct.sohQty;
            });
            this.disableCheckPrint();
        }
    }
};
</script>
<style scoped>
#tagContent {
    visibility: hidden;
}
</style>
